import {BaseStore, globalStore} from "../../stem-core/state/Store";
import {apiClient, type MerchantScopedRequest} from "../connection/BlinkApiClient";
import type {StoreId} from "../../stem-core/state/State";
import {field} from "../../stem-core/state/StoreField";
import {Money} from "../../stem-core/localization/Money";
import {BaseEnum, makeEnum} from "../../stem-core/state/BaseEnum";
import {formatPercent} from "../../stem-core/base/Formatting";
import {StemDate} from "../../stem-core/time/Date";
import {EditableObjectStatus} from "./misc/GenericEnums";
import {Merchant} from "./MerchantStore";


@makeEnum
export class SubscriptionDiscountScope extends BaseEnum {
    static ALL_OFFERS = {name: "All subscription"};
    static TARGETED_COVERAGES = {name: "Selected subscription tiers"};
    static TARGETED_OFFERS = {name: "Selected subscription plans"};
}

@makeEnum
export class SubscriptionDiscountApplyPolicy extends BaseEnum {
    static BEFORE_SHIPPING;
    static AFTER_SHIPPING;
}

@makeEnum
export class SubscriptionDiscountType extends BaseEnum {
    static DISCOUNT_PERCENT;
    static DISCOUNT_AMOUNT;
    static FIXED_PRICE;
}


@globalStore
export class SubscriptionDiscount extends BaseStore("SubscriptionDiscount") {
    // TODO: missing some fields here
    @field("Merchant") merchant: Merchant;
    @field(Date) deletedAt: StemDate;
    @field(SubscriptionDiscountScope) scope: SubscriptionDiscountScope;
    @field(Array) targetOfferIds: number[];
    @field(Array) targetCoverageIds: number[];
    @field(String) name: string;
    @field(String) code: string;
    @field(Date) startDate: StemDate;
    @field(Date) expireDate: StemDate;
    @field(SubscriptionDiscountApplyPolicy) applyPolicy: SubscriptionDiscountApplyPolicy;
    @field(Number) numCyclesDiscounted: number;
    @field(String) discountPercent: string; // TODO Need to support Decimal
    @field(Number) discountAmount: number;
    @field(Number) discountFixedPrice: number;

    // TODO @cleanups deprecate
    getCode(): string {
        return this.code;
    }

    getDiscountValue(): string {
        if (this.discountPercent != null) {
            const numericalPercent = parseFloat(this.discountPercent);
            return `${formatPercent(numericalPercent)} off`;
        }
        const currency = this.merchant.getCurrency();
        if (this.discountAmount != null) {
            return `${new Money(this.discountAmount, currency)} off`;
        }
        return `Reduced to ${new Money(this.discountFixedPrice, currency)}`;
    }

    // TODO @branch deprecate
    getMerchant(): Merchant {
        return this.merchant;
    }

    getStatus(): EditableObjectStatus {
        if (this.deletedAt) {
            return EditableObjectStatus.INACTIVE;
        }
        const now = StemDate.now();
        if (this.startDate?.isAfter(now)) {
            return EditableObjectStatus.DRAFT;
        }
        if (this.expireDate?.isBefore(now)) {
            return EditableObjectStatus.EXPIRED;
        }
        return EditableObjectStatus.ACTIVE;
    }

    isActive(): boolean {
        return this.getStatus() === EditableObjectStatus.ACTIVE;
    }
}


// CreateSubscriptionDiscountRequest (api/merchants/views.py). name and scope are required here, narrowed
// from the optional ones its base declares
export interface CreateSubscriptionDiscountRequest extends MerchantScopedRequest {
    name: string;
    scope: SubscriptionDiscountScope;
    applyPolicy: SubscriptionDiscountApplyPolicy;
    code?: string;
    startDate?: StemDate;
    expireDate?: StemDate;
    targetOfferIds?: StoreId[];
    targetCoverageIds?: StoreId[];
    numCyclesDiscounted?: number;
    discountPercent?: number;
    discountAmount?: number;
    discountFixedPrice?: number;
}

export async function apiMerchantCreateSubscriptionDiscount(request: CreateSubscriptionDiscountRequest): Promise<SubscriptionDiscount> {
    const response = await apiClient.post("/merchant/create_subscription_discount", request);
    return SubscriptionDiscount.loadObject(response);
}

// EditSubscriptionDiscountRequest, which carries the id and leaves the rest optional
export interface EditSubscriptionDiscountRequest extends MerchantScopedRequest {
    subscriptionDiscountId: StoreId;
    name?: string;
    scope?: SubscriptionDiscountScope;
    code?: string;
    startDate?: StemDate;
    expireDate?: StemDate;
    targetOfferIds?: StoreId[];
    targetCoverageIds?: StoreId[];
}

export async function apiMerchantEditSubscriptionDiscount(request: EditSubscriptionDiscountRequest): Promise<any> {
    return apiClient.post("/merchant/edit_subscription_discount", request);
}

// Deliberately untyped: /merchant/archive_subscription_discount has no handler in the API at all, so
// there is no request class to type it against. See Backlog.md
export async function apiMerchantArchiveSubscriptionDiscount(request: any): Promise<any> {
    return apiClient.post("/merchant/archive_subscription_discount", request);
}
